import React, { useState, useRef } from 'react';
import { Upload, FileText, File, X, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { parseDocument, validateFile, getWordCount } from './documentParser';
import { v4 as uuidv4 } from 'uuid';
import './Documents.css';

const DocumentUploadPanel = ({ onDocumentParsed, onCancel }) => {
  const [dragActive, setDragActive] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [parsedDoc, setParsedDoc] = useState(null);
  const [docTitle, setDocTitle] = useState('');
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const processFile = async (file) => {
    setError(null);
    setParsedDoc(null);

    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setParsing(true);
    try {
      const text = await parseDocument(file);
      const wordCount = getWordCount(text);
      
      setParsedDoc({
        id: uuidv4(),
        fileName: file.name,
        fileType: file.name.split('.').pop().toLowerCase(),
        fileSize: file.size,
        text,
        wordCount,
        uploadedAt: new Date().toISOString()
      });
      setDocTitle(file.name.replace(/\.[^/.]+$/, ''));
    } catch (err) {
      setError(err.message);
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleSave = () => {
    if (parsedDoc && docTitle.trim()) {
      onDocumentParsed({
        ...parsedDoc,
        title: docTitle.trim()
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <File size={20} className="file-icon pdf" />;
      case 'docx': return <FileText size={20} className="file-icon docx" />;
      case 'xlsx':
      case 'xls': return <FileText size={20} className="file-icon xlsx" />;
      default: return <FileText size={20} className="file-icon txt" />;
    }
  };

  return (
    <div className="document-upload-panel">
      <div className="upload-header">
        <h2><Upload size={24} /> Upload Qualitative Data Document for Analysis</h2>
        <p>Upload interview transcripts, focus group notes, or other qualitative data documents</p>
      </div>

      {!parsedDoc && (
        <div 
          className={`upload-dropzone ${dragActive ? 'active' : ''} ${parsing ? 'parsing' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.docx,.pdf,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {parsing ? (
            <div className="parsing-indicator">
              <Loader size={40} className="spinner" />
              <p>Parsing document...</p>
            </div>
          ) : (
            <>
              <Upload size={48} className="upload-icon" />
              <p className="upload-main-text">Drag and drop your file here</p>
              <p className="upload-sub-text">or click to browse</p>
              <div className="supported-formats">
                <span className="format-badge txt">.txt</span>
                <span className="format-badge docx">.docx</span>
                <span className="format-badge pdf">.pdf</span>
                <span className="format-badge xlsx">.xlsx</span>
              </div>
              <p className="size-limit">Maximum file size: 5MB</p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="upload-error">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}

      {parsedDoc && (
        <div className="parsed-document-preview">
          <div className="preview-header">
            <CheckCircle size={24} className="success-icon" />
            <span>Document parsed successfully!</span>
          </div>

          <div className="document-info">
            <div className="info-row">
              {getFileIcon(parsedDoc.fileType)}
              <span className="file-name">{parsedDoc.fileName}</span>
              <span className="file-size">{formatFileSize(parsedDoc.fileSize)}</span>
            </div>
            <div className="word-count">
              <strong>{parsedDoc.wordCount.toLocaleString()}</strong> words
            </div>
          </div>

          <div className="document-title-input">
            <label>Document Title</label>
            <input
              type="text"
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              placeholder="Enter a title for this document"
            />
          </div>

          <div className="text-preview">
            <label>Preview</label>
            <div className="preview-text">
              {parsedDoc.text.substring(0, 500)}
              {parsedDoc.text.length > 500 && '...'}
            </div>
          </div>

          <div className="upload-actions">
            <button className="btn-secondary" onClick={() => setParsedDoc(null)}>
              Upload Different File
            </button>
            <button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={!docTitle.trim()}
            >
              Save & Analyze
            </button>
          </div>
        </div>
      )}

      {!parsedDoc && (
        <div className="upload-footer">
          <button className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadPanel;
